import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Info, Moon, Sun, X, FileText, Image as ImageIcon, Grid, Layers, GripVertical } from "lucide-react";
import { jsPDF } from "jspdf";
import heic2any from "heic2any";
import ToolsNav from "../components/ToolsNav";
import { AdContainer, ResponsiveAd, StickyMobileAd, DesktopOnlyAd } from "../components/AdPlacement";

const PAGE_SIZES = {
  "a4": { name: "A4", width: 210, height: 297 },
  "letter": { name: "Letter", width: 215.9, height: 279.4 },
  "a3": { name: "A3", width: 297, height: 420 },
  "a5": { name: "A5", width: 148, height: 210 },
};

const LAYOUT_OPTIONS = {
  "one-per-page": { name: "One Image Per Page", description: "Each image on its own page", icon: Layers },
  "grid-2x2": { name: "2×2 Grid", description: "4 images per page", icon: Grid },
  "grid-3x3": { name: "3×3 Grid", description: "9 images per page", icon: Grid },
  "fit-many": { name: "Fit Multiple", description: "Auto-fit images on pages", icon: GripVertical },
};

function ImagesToPdf({ embedded = false }) {
  const [images, setImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pageSize, setPageSize] = useState("a4");
  const [orientation, setOrientation] = useState("portrait");
  const [layout, setLayout] = useState("one-per-page");
  const [imageQuality, setImageQuality] = useState("high");
  const [margin, setMargin] = useState("10");
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = "Images to PDF - Convert Multiple Images to PDF - KayTool";
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Convert multiple images to PDF. Choose layout options - one image per page or grid layout. Support for JPG, PNG, WebP, HEIC, SVG, TIFF. Free online converter.');
  }, []);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    await processFiles(files);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await processFiles(files);
    }
  };

  const processFiles = async (files) => {
    const imageFiles = files.filter(f => 
      f.type.startsWith('image/') || 
      f.name.toLowerCase().endsWith('.heic') || 
      f.name.toLowerCase().endsWith('.heif') ||
      f.name.toLowerCase().endsWith('.svg') ||
      f.name.toLowerCase().endsWith('.tiff') ||
      f.name.toLowerCase().endsWith('.tif')
    );
    
    if (imageFiles.length === 0) {
      toast.error("Please select valid image files");
      return;
    }

    setIsProcessing(true);
    setProgress(0);

    const newImages = [];

    for (let i = 0; i < imageFiles.length; i++) {
      let file = imageFiles[i];
      
      // Convert HEIC/HEIF to JPEG
      if (file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif')) {
        try {
          const convertedBlob = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.95 });
          file = new File([convertedBlob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
        } catch (error) {
          toast.error(`Failed to convert ${file.name}`);
          continue;
        }
      }

      const preview = URL.createObjectURL(file);
      const dimensions = await getImageDimensions(file);

      newImages.push({
        id: Date.now() + i,
        name: file.name,
        file: file,
        preview: preview,
        width: dimensions.width,
        height: dimensions.height,
        size: file.size,
      });

      setProgress(Math.round(((i + 1) / imageFiles.length) * 100));
    }

    setImages(prev => [...prev, ...newImages]);
    setIsProcessing(false);
    toast.success(`Added ${newImages.length} image${newImages.length > 1 ? 's' : ''}`);
  };

  const getImageDimensions = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
        URL.revokeObjectURL(img.src);
      };
      img.onerror = () => resolve({ width: 100, height: 100 });
      img.src = URL.createObjectURL(file);
    });
  };

  const loadImageAsDataUrl = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const convertToPdf = async () => {
    if (images.length === 0) {
      toast.error("Please add images first");
      return;
    }

    setIsProcessing(true);
    setProgress(0);

    try {
      const page = PAGE_SIZES[pageSize];
      const isLandscape = orientation === "landscape";
      const pageWidth = isLandscape ? page.height : page.width;
      const pageHeight = isLandscape ? page.width : page.height;
      const marginSize = parseInt(margin);
      
      const pdf = new jsPDF({
        orientation: isLandscape ? 'landscape' : 'portrait',
        unit: 'mm',
        format: [pageWidth, pageHeight]
      });

      const quality = imageQuality === "high" ? 1.0 : imageQuality === "medium" ? 0.7 : 0.5;

      if (layout === "one-per-page") {
        // One image per page
        for (let i = 0; i < images.length; i++) {
          if (i > 0) pdf.addPage();
          
          const dataUrl = await loadImageAsDataUrl(images[i].file);
          const imgRatio = images[i].width / images[i].height;
          const availableWidth = pageWidth - (marginSize * 2);
          const availableHeight = pageHeight - (marginSize * 2);
          const availableRatio = availableWidth / availableHeight;
          
          let imgWidth, imgHeight;
          if (imgRatio > availableRatio) {
            imgWidth = availableWidth;
            imgHeight = availableWidth / imgRatio;
          } else {
            imgHeight = availableHeight;
            imgWidth = availableHeight * imgRatio;
          }
          
          const x = (pageWidth - imgWidth) / 2;
          const y = (pageHeight - imgHeight) / 2;
          
          pdf.addImage(dataUrl, 'JPEG', x, y, imgWidth, imgHeight, undefined, quality > 0.7 ? 'FAST' : 'MEDIUM');
          setProgress(Math.round(((i + 1) / images.length) * 100));
        }
      } else if (layout === "grid-2x2" || layout === "grid-3x3") {
        const cols = layout === "grid-2x2" ? 2 : 3;
        const rows = cols;
        const imagesPerPage = cols * rows;
        const cellWidth = (pageWidth - (marginSize * 2) - (marginSize * (cols - 1))) / cols;
        const cellHeight = (pageHeight - (marginSize * 2) - (marginSize * (rows - 1))) / rows;
        
        for (let i = 0; i < images.length; i++) {
          const pageIndex = Math.floor(i / imagesPerPage);
          const positionOnPage = i % imagesPerPage;
          
          if (positionOnPage === 0 && i > 0) pdf.addPage();
          
          const col = positionOnPage % cols;
          const row = Math.floor(positionOnPage / cols);
          
          const dataUrl = await loadImageAsDataUrl(images[i].file);
          const imgRatio = images[i].width / images[i].height;
          const cellRatio = cellWidth / cellHeight;
          
          let imgWidth, imgHeight;
          if (imgRatio > cellRatio) {
            imgWidth = cellWidth;
            imgHeight = cellWidth / imgRatio;
          } else {
            imgHeight = cellHeight;
            imgWidth = cellHeight * imgRatio;
          }
          
          const cellX = marginSize + (col * (cellWidth + marginSize));
          const cellY = marginSize + (row * (cellHeight + marginSize));
          const x = cellX + (cellWidth - imgWidth) / 2;
          const y = cellY + (cellHeight - imgHeight) / 2;
          
          pdf.addImage(dataUrl, 'JPEG', x, y, imgWidth, imgHeight, undefined, quality > 0.7 ? 'FAST' : 'MEDIUM');
          setProgress(Math.round(((i + 1) / images.length) * 100));
        }
      } else {
        // Fit multiple - auto layout
        let currentY = marginSize;
        let currentX = marginSize;
        let rowHeight = 0;
        const availableWidth = pageWidth - (marginSize * 2);
        
        for (let i = 0; i < images.length; i++) {
          const dataUrl = await loadImageAsDataUrl(images[i].file);
          const imgRatio = images[i].width / images[i].height;
          
          // Target height of 60mm for auto layout
          let imgHeight = 60;
          let imgWidth = imgHeight * imgRatio;
          
          // If image is too wide, scale it down
          if (imgWidth > availableWidth) {
            imgWidth = availableWidth;
            imgHeight = imgWidth / imgRatio;
          }
          
          // Check if we need a new row
          if (currentX + imgWidth > pageWidth - marginSize) {
            currentX = marginSize;
            currentY += rowHeight + marginSize;
            rowHeight = 0;
          }
          
          // Check if we need a new page
          if (currentY + imgHeight > pageHeight - marginSize) {
            pdf.addPage();
            currentX = marginSize;
            currentY = marginSize;
            rowHeight = 0;
          }
          
          pdf.addImage(dataUrl, 'JPEG', currentX, currentY, imgWidth, imgHeight, undefined, quality > 0.7 ? 'FAST' : 'MEDIUM');
          
          currentX += imgWidth + marginSize;
          rowHeight = Math.max(rowHeight, imgHeight);
          
          setProgress(Math.round(((i + 1) / images.length) * 100));
        }
      }

      pdf.save(`images_${Date.now()}.pdf`);
      toast.success("PDF created successfully!");
    } catch (error) {
      console.error("PDF creation error:", error);
      toast.error("Failed to create PDF");
    }

    setIsProcessing(false);
  };

  const removeImage = (id) => {
    setImages(prev => prev.filter(img => img.id !== id));
  };

  const clearAll = () => {
    setImages([]);
  };

  const moveImage = (index, direction) => {
    const newImages = [...images];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= images.length) return;
    [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
    setImages(newImages);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-red-50 to-orange-50'}`}>
      <Toaster position="top-center" richColors />
      
      {!embedded && (
        <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <a href="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
            </a>
            <div className="flex items-center gap-4">
              <ToolsNav />
              <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
                <Info className="w-3 h-3" /> 100% Local Processing
              </Badge>
              <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </header>
      )}

      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-full mb-4">
            <ImageIcon className="w-5 h-5" />
            <span className="font-medium">Images to PDF</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            Convert Images to PDF
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Combine multiple images into a single PDF. Choose layout, page size, and quality options.
          </p>
        </div>

        {/* Settings Card */}
        <Card className="mb-6 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-6 space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Page Size & Orientation */}
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Page Size</Label>
                  <Select value={pageSize} onValueChange={setPageSize}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(PAGE_SIZES).map(([key, val]) => (
                        <SelectItem key={key} value={key}>{val.name} ({val.width}×{val.height}mm)</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Orientation</Label>
                  <div className="flex gap-2">
                    <Button
                      variant={orientation === "portrait" ? "default" : "outline"}
                      onClick={() => setOrientation("portrait")}
                      className={`flex-1 ${orientation === "portrait" ? "bg-red-600 hover:bg-red-700" : ""}`}
                    >
                      Portrait
                    </Button>
                    <Button
                      variant={orientation === "landscape" ? "default" : "outline"}
                      onClick={() => setOrientation("landscape")}
                      className={`flex-1 ${orientation === "landscape" ? "bg-red-600 hover:bg-red-700" : ""}`}
                    >
                      Landscape
                    </Button>
                  </div>
                </div>
              </div>

              {/* Layout & Quality */}
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Layout</Label>
                  <Select value={layout} onValueChange={setLayout}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(LAYOUT_OPTIONS).map(([key, val]) => (
                        <SelectItem key={key} value={key}>{val.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">{LAYOUT_OPTIONS[layout].description}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Image Quality</Label>
                  <Select value={imageQuality} onValueChange={setImageQuality}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High (Larger file)</SelectItem>
                      <SelectItem value="medium">Medium (Balanced)</SelectItem>
                      <SelectItem value="low">Low (Smaller file)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Margin */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Page Margin: {margin}mm</Label>
              <input
                type="range"
                min="0"
                max="30"
                value={margin}
                onChange={(e) => setMargin(e.target.value)}
                className="w-full"
              />
            </div>
          </CardContent>
        </Card>

        {/* Upload Area */}
        <Card 
          className={`border-2 border-dashed transition-all duration-300 dark:bg-slate-800 ${
            isDragging ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-slate-300 dark:border-slate-600'
          }`}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <CardContent className="p-12 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.heic,.heif,.svg,.tiff,.tif"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />
            <div 
              className="cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-zone"
            >
              <Upload className="w-16 h-16 mx-auto mb-4 text-red-500" />
              <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                Drop images or click to upload
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mb-4">
                Supports JPG, PNG, WebP, GIF, HEIC, SVG, TIFF
              </p>
              <Badge variant="secondary">Multiple files supported</Badge>
            </div>
          </CardContent>
        </Card>

        {isProcessing && (
          <Card className="mt-6 dark:bg-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Progress value={progress} className="h-3" />
                </div>
                <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                  {progress}%
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {images.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                Images ({images.length}) - Drag to reorder
              </h3>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={clearAll} className="text-red-600">
                  <X className="w-4 h-4 mr-1" /> Clear All
                </Button>
                <Button 
                  onClick={convertToPdf} 
                  disabled={isProcessing}
                  data-testid="convert-button"
                  className="bg-gradient-to-r from-red-500 to-red-600 hover:opacity-90"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Convert to PDF
                </Button>
              </div>
            </div>

            <DesktopOnlyAd slot="above-results" size="large-rectangle" className="my-4" />

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {images.map((image, index) => (
                <Card key={image.id} className="dark:bg-slate-800 overflow-hidden group" data-testid={`image-card-${image.id}`}>
                  <div className="aspect-square relative bg-slate-100 dark:bg-slate-700">
                    <img
                      src={image.preview}
                      alt={image.name}
                      className="w-full h-full object-contain"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      {index > 0 && (
                        <Button size="icon" variant="secondary" className="w-8 h-8" onClick={() => moveImage(index, -1)}>
                          ←
                        </Button>
                      )}
                      <Button size="icon" variant="destructive" className="w-8 h-8" onClick={() => removeImage(image.id)}>
                        <X className="w-4 h-4" />
                      </Button>
                      {index < images.length - 1 && (
                        <Button size="icon" variant="secondary" className="w-8 h-8" onClick={() => moveImage(index, 1)}>
                          →
                        </Button>
                      )}
                    </div>
                    <Badge className="absolute bottom-2 left-2 bg-black/70 text-xs">
                      {index + 1}
                    </Badge>
                  </div>
                  <CardContent className="p-2">
                    <p className="text-xs text-slate-600 dark:text-slate-400 truncate">{image.name}</p>
                    <p className="text-xs text-slate-400">{formatSize(image.size)}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {[
            { icon: ImageIcon, title: "Multiple Formats", desc: "JPG, PNG, WebP, HEIC, SVG, TIFF supported" },
            { icon: Grid, title: "Layout Options", desc: "One per page, grid, or auto-fit layouts" },
            { icon: Info, title: "100% Private", desc: "All processing happens in your browser" }
          ].map((feature, i) => (
            <Card key={i} className="dark:bg-slate-800">
              <CardContent className="p-6 text-center">
                <feature.icon className="w-10 h-10 mx-auto mb-3 text-red-500" />
                <h4 className="font-semibold mb-2 dark:text-white">{feature.title}</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">{feature.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

export default ImagesToPdf;
